<script>
    import hosted from '../private/hosted';
    import { scrollProps } from '../private/util';
    import $device from '../../prototype/device';
    import $util from '../../prototype/util';

    const scrollTimeout = 160;    // ms
    const shadowSpread = 24;      // px
    const openThreshold = 64;     // px
    const handleHeight = $build.platform == 'ios' ? 16 : 8;

    /**
     * This is an overlay docked to a window side, that can be modeless or modal when opened.
     */
    export default {
        name: 'ib-drawer',

        mixins: [
            hosted
        ],

        inheritAttrs: false,

        props: {
            /**
             * If `true`, this is a modal panel, blocking interaction with other user interface
             * elements.
             */
            modal: Boolean,
            /**
             * Width in open position, not including handle, in any CSS units.
             * By default, will fit to content.
             */
            width: String,
            /**
             * Width in closed position, not including handle, in pixels.
             * By default, 0 (fully closed).
             */
            minWidth: Number,
            /**
             * Height in open position, not including handle, in any CSS units.
             * By default, will fit to content.
             */
            height: String,
            /**
             * Height in closed position, not including handle, in pixels.
             * By default, 0 (fully closed).
             */
            minHeight: Number,
            /**
             * Requested position relative to window [top, right, bottom, left, start,
             * end]; default is bottom. If an anchor is specified, the position may be inverted
             * to fit available window space.
             */
            position: {
                type: String,
                default: 'bottom'
            },
            /**
             * If `true`, display a handle (for iOS) or equivalent empty space (for other
             * platforms). This only applies to top and bottom drawers.
             */
            handle: {
                type: Boolean,
                default: true
            },
            /**
             * If `true`, show the drawer, otherwise hide it. Visibility can also be manipulated
             * using the `show` and `hide` methods.
             */
            visible: {
                type: Boolean,
                default: true
            },
            /**
             * If `true`, close this panel when clicking anywhere outside of it.
             */
            autoclose: Boolean
        },

        data() {
            return {
                isVisible: true,
                isOpened: false,
                key: 0
            };
        },

        computed: {
            sp() {
                return scrollProps();
            },

            railStyle() {
                return this.position == 'top' || this.position == 'bottom' ? {
                    marginInlineEnd: -(this.sp.barWidth || 16) + 'px'
                } : {
                    marginBottom: -(this.sp.barHeight || 16) + 'px'
                };
            },

            paneStyle() {
                switch (this.position) {
                case 'top':
                    return {
                        height: this.height,
                        marginInlineEnd: (this.sp.barWidth ? 0 : 16) + 'px',
                        marginBottom: -(this.minHeight + handleHeight | 0) + 'px'
                    };
                case 'bottom':
                    return {
                        height: this.height,
                        marginInlineEnd: (this.sp.barWidth ? 0 : 16) + 'px',
                        marginTop: -(this.minHeight + handleHeight | 0) + 'px'
                    };
                case 'left':
                    return {
                        width: this.width,
                        marginBottom: (this.sp.barHeight ? 0 : 16) + 'px',
                        marginRight: -(this.minWidth | 0) + 'px'
                    };
                case 'right':
                    return {
                        width: this.width,
                        marginBottom: (this.sp.barHeight ? 0 : 16) + 'px',
                        marginLeft: -(this.minWidth | 0) + 'px'
                    };
                }
            }
        },

        watch: {
            visible: {
                handler(visible) {
                    if (visible) {
                        this.show();
                    } else {
                        this.hide();
                    }
                },
                immediate: true
            },
            isOpened(opened) {
                if (!opened) {
                    const el = this.scrollingChild();
                    if (el) {
                        el.scrollTo({
                            top: 0,
                            behavior: 'smooth'
                        });
                    }
                }
            }
        },

        created() {
            // not reactive
            this.backdropOpacity = 0;
            this.scrolling = null;
            this.scrollCnt = 0;
        },

        mounted() {
            this.$nextTick(() => {
                this.close('auto');
                if (window.ResizeObserver && this.position == 'bottom') {
                    this.rso = new ResizeObserver(this.alignBottom);
                    this.drwrp = this.host.$refs['_drwrp' + this._uid];
                    this.scrollTop = this.drwrp.parentElement.scrollTop;
                    this.rso.observe(this.drwrp);
                }
                if ($device.ios) {
                    const rail = this.host.$refs['_drwrr' + this._uid];
                    rail.addEventListener('touchmove', this.preventscroll, { passive: false });
                    rail.addEventListener('touchforcechange', this.preventscroll, { passive: false });
                }
            });
        },

        render(h) {
            // empty
        },

        updated() {
            this.key++;
            if (this.rso) {
                this.$nextTick(() => {
                    const drwrp = this.host.$refs['_drwrp' + this._uid];
                    if (this.drwrp !== drwrp) {
                        this.rso.unobserve(this.drwrp);
                        this.rso.observe(this.drwrp = drwrp);
                    }
                });
            }
        },

        beforeDestroy() {
            if (this.scrollTo) {
                clearTimeout(this.scrollTo);
            }
            if (this.rso) {
                this.rso.disconnect();
            }
        },

        methods: {
            /** Show the drawer programmatically */
            show() {
                if (!this.isVisible) {
                    this.isVisible = true;
                    this.close();
                }
            },

            /** Hide the drawer programmatically */
            hide() {
                if (this.isVisible) {
                    this.isVisible = false;
                    this.isOpened = false;
                    if (this.host) {
                        const backdrop = this.host.$refs['_drwrb' + this._uid];
                        if (backdrop) {
                            this.backdropOpacity =
                            backdrop.style.opacity = 0;
                        }
                    }
                }
            },

            open(behavior = 'smooth') {
                if (!this.isOpened) {
                    const el = this.host.$refs['_drwr' + this._uid];
                    if (el) {
                        const rail = el.lastChild;
                        rail.scrollTo({
                            top: rail.scrollHeight - rail.clientHeight,
                            behavior
                        });
                        this.isOpened = true;
                    }
                }
            },

            close(behavior = 'smooth') {
                const el = this.host.$refs['_drwr' + this._uid];
                if (el) {
                    const rail = el.firstChild;
                    rail.scrollTo({
                        top: 0,
                        behavior
                    });
                    this.isOpened = false;
                }
            },

            /** Maintain bottom alignment on resize */
            alignBottom(entries) {
                const entry = entries[entries.length - 1];
                if (entry) {
                    if (this.scrollCnt < 2) {
                        const rail = entry.target.parentElement;
                        rail.scrollTop = this.isOpened ? rail.scrollHeight - rail.clientHeight : 0;
                    }
                }
            },

            scrollStart(el) {
                el.classList.add('_drwr-scroll');
                this.scrolling = el;
                this.scrollCnt = 0;
            },

            scrollStop() {
                const el = this.scrolling;
                this.scrolling = null;
                this.scrollTo = 0;
                this.scrollCnt = 0;
                el.classList.remove('_drwr-scroll');
                el.style.height = this.clipHeight(el.lastChild, 0);
            },

            scroll(e) {
                const scrollTop = e.target.scrollTop;
                if (this.modal) {
                    const backdrop = this.host.$refs['_drwrb' + this._uid];
                    if (backdrop) {
                        this.backdropOpacity =
                        backdrop.style.opacity =
                            .2 * Math.min(1, Math.max(0, (scrollTop - openThreshold) / (e.target.scrollHeight - e.target.clientHeight - openThreshold)));
                    }
                }
                if (!this.scrolling) {
                    this.scrollStart(e.target.parentElement);
                }
                if (this.scrollTo) {
                    clearTimeout(this.scrollTo);
                    this.scrollTo = 0;
                }
                this.scrollTo = setTimeout(this.scrollStop, scrollTimeout);
                this.scrollLast = e.timeStamp;
                this.scrollCnt++;
                e.target.parentElement.style.height = this.clipHeight(e.target, shadowSpread);
                // isOpened may need to retain old value briefly for alignBottom
                setTimeout(() => {
                    this.isOpened = scrollTop > openThreshold;
                });
            },

            clipHeight(rail, margin) {
                const scrollTop = Math.max(0, rail.scrollTop);
                if ((this.modal || this.scrolling) && scrollTop >= openThreshold) {
                    return '100vh';
                } else {
                    return (this.minHeight + handleHeight | 0) + margin + scrollTop + 'px';
                }
            },

            touchstart(e) {
                if (!$util.isChildOrSelf(e.target, this.host.$refs['_drwrp' + this._uid])) {
                    e.preventDefault();
                } else if ($device.ios && this.scrolling && this.isOpened) {
                    // Attempt to prevent swipe gesture from moving rail when user intends to scroll pane content
                    // Hopefully in the future this can be accomplished gracefully
                    this.touchY = e.pageY;
                }
            },

            touchend(e) {
                this.touchY = null;
            },

            preventscroll(e) {
                if (this.touchY != null) {
                    if (this.touchY > e.pageY) {
                        e.preventDefault();
                        const el = this.scrollingChild();
                        if (el) {
                            el.scrollTop += this.touchY - e.pageY;
                        }
                    }
                    this.touchY = e.pageY;
                }
            },

            click() {
                this.open();
            },

            scrollingChild() {
                const scrolling = {
                    auto: true,
                    overlay: true,
                    scroll: true
                };
                function test(parent) {
                    const children = parent.children;
                    for (let i = 0; i < children.length; i++) {
                        const el = children[i];
                        if (scrolling[getComputedStyle(el).getPropertyValue('overflow-y')] && el.scrollHeight > el.clientHeight) {
                            return el;
                        } else {
                            return test(el);
                        }
                    }
                }
                return test(this.host.$refs['_drwrp' + this._uid]);
            },

            renderSelf(h, host) {
                // Pass reactivity
                void this.key;
                this.host = host;

                const modal = this.modal;
                return h('transition', {
                    'attrs': {
                        'name': '_tr-drawer',
                        'appear': true
                    }
                }, [
                    h('div', {
                        'ref': '_drwr' + this._uid,
                        'staticClass': '_drwr _con',
                        'class': {
                            ['_p' + this.position]: true,
                            '_drwr-scroll': !!this.scrolling,
                            '_drwr-modal': modal,
                            '_drwr-open': this.isOpened,
                            '_drwr-close': !this.isOpened
                        },
                        'style': {
                            height: (this.minHeight + handleHeight | 0) + 'px'
                        },
                        'directives': [
                            {
                                name: 'show',
                                rawName: 'v-show',
                                value: this.isVisible,
                                expression: 'show'
                            }
                        ]
                    }, [
                        modal ? h('div', {
                            'ref': '_drwrb' + this._uid,
                            'staticClass': '_drwr-backdrop',
                            'style': {
                                opacity: this.backdropOpacity
                            }
                        }) : void 0,
                        h('div', {
                            'ref': '_drwrr' + this._uid,
                            'staticClass': '_drwr-rail',
                            'style': this.railStyle,
                            'on': {
                                scroll: this.scroll,
                                touchstart: this.touchstart,
                                touchend: this.touchend
                            }
                        }, [
                            h('div', {
                                'ref': '_drwrp' + this._uid,
                                'staticClass': '_drwr-pane',
                                'class': {
                                    '_drwr-handle': this.handle && this.position == 'top' || this.position == 'bottom'
                                },
                                'attrs': {
                                    'role': 'dialog',
                                    'aria-modal': modal,
                                    ...this.$attrs
                                },
                                'style': this.paneStyle,
                                'on': {
                                    click: this.click
                                }
                            }, [
                                h('div', {
                                    'staticClass': '_drwr-def _row grow'
                                }, [
                                    this.$slots.default
                                ])
                            ])
                        ])
                    ])
                ]);
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._drwr {
        position: fixed;
        overflow: hidden;
        .shadow-med();
        z-index: @z-modeless-med;

        &._ptop {
            top: 0;
            left: 0;
            right: 0;
            & when (isunit(@drawer-border-radius, px)) {
                border-radius: 0 0 @drawer-border-radius @drawer-border-radius;
            }
            > ._drwr-rail {
                top: 0;
                left: 0;
                right: 0;
                height: 100vh;
                overflow-x: hidden;
                overflow-y: scroll;
                &::before {
                    bottom: 0;
                }
            }
            ._drwr-pane {
                bottom: 100%;
                left: 0;
                right: 0;
                max-height: calc(~'100% - 64px');
                & when (isunit(@drawer-border-radius, px)) {
                    border-radius: 0 0 @drawer-border-radius @drawer-border-radius;
                }
            }
            ._drwr-handle {
                .drawer-handle-top();
            }
        }
        &._pbottom {
            bottom: 0;
            left: 0;
            right: 0;
            & when (isunit(@drawer-border-radius, px)) {
                border-radius: @drawer-border-radius @drawer-border-radius 0 0;
            }
            > ._drwr-rail {
                bottom: 0;
                left: 0;
                right: 0;
                height: 100vh;
                overflow-x: hidden;
                overflow-y: scroll;
                &::before {
                    top: 0;
                }
            }
            ._drwr-pane {
                top: 100%;
                left: 0;
                right: 0;
                max-height: calc(~'100% - 64px');
                & when (isunit(@drawer-border-radius, px)) {
                    border-radius: @drawer-border-radius @drawer-border-radius 0 0;
                }
            }
            ._drwr-handle {
                .drawer-handle-bottom();
            }
        }
        &._pleft {
            left: 0;
            top: 0;
            bottom: 0;
            & when (isunit(@drawer-border-radius, px)) {
                border-radius: 0 @drawer-border-radius @drawer-border-radius 0;
            }
            > ._drwr-rail {
                left: 0;
                top: 0;
                bottom: 0;
                width: 100vw;
                overflow-x: scroll;
                overflow-y: hidden;
                &::before {
                    right: 0;
                }
            }
            ._drwr-pane {
                right: 100%;
                top: 0;
                bottom: 0;
                max-width: calc(~'100% - 64px');
                & when (isunit(@drawer-border-radius, px)) {
                    border-radius: 0 @drawer-border-radius @drawer-border-radius 0;
                }
            }
        }
        &._pright {
            right: 0;
            top: 0;
            bottom: 0;
            & when (isunit(@drawer-border-radius, px)) {
                border-radius: @drawer-border-radius 0 0 @drawer-border-radius;
            }
            > ._drwr-rail {
                right: 0;
                top: 0;
                bottom: 0;
                width: 100vw;
                overflow-x: scroll;
                overflow-y: hidden;
                &::before {
                    left: 0;
                }
            }
            ._drwr-pane {
                left: 100%;
                top: 0;
                bottom: 0;
                max-width: calc(~'100% - 64px');
                & when (isunit(@drawer-border-radius, px)) {
                    border-radius: @drawer-border-radius 0 0 @drawer-border-radius;
                }
            }
        }

        &-backdrop {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background-color: #000006;
        }
        &-rail {
            position: absolute;
            -webkit-scroll-snap-destination: 0% 0%;
            -webkit-scroll-snap-points-x: repeat(100%);
            -webkit-overflow-scrolling: touch;
            -webkit-scroll-snap-type: mandatory;
            -ms-scroll-snap-type: mandatory;
            scroll-snap-type: y mandatory;
            scroll-snap-points-y: repeat(100%);

            /** Scroll snap point for closed position */
            &::before {
                content: '';
                position: absolute;
                display: block;
                scroll-snap-align: start;
                width: 1px;
                height: 1px;
            }
        }
        &-scroll,
        &-modal._drwr-open {
            box-shadow: none;
            ._drwr-pane {
                .shadow-med();
            }
        }
        &-pane {
            position: absolute;
            display: flex;
            flex-direction: column;
            background: @bg-modeless;
            overflow: hidden;
            scroll-snap-align: end;
        }
        &-close {
            ._row.grow,
            ._row.shrink {
                overflow: hidden;
            }
        }
    }

    ._tr-drawer {
        &-enter-active,
        &-leave-active {
            transition: transform .5s;
        }
        &-enter,
        &-leave-to {
            &._ptop {
                transform: translateY(-100%);
            }
            &._pbottom {
                transform: translateY(100%);
            }
            &._pleft {
                transform: translateX(-100%);
            }
            &._pright {
                transform: translateX(100%);
            }
        }
    }
</style>
